import { useCallback, useEffect, useRef, useState } from "react";

import type { LoadAdditionalItems } from "../types";

type BackfillState = {
    extraItems: unknown[];
    isLoading: boolean;
    error: Error | null;
};

export const useBackfill = ({
    seedCount,
    loader,
    keyExtractor = defaultKeyExtractor
}: {
    seedCount: number;
    loader: LoadAdditionalItems | null;
    keyExtractor?: (item: unknown) => string | undefined;
}): BackfillState => {
    const [state, setState] = useState<BackfillState>({
        extraItems: [],
        isLoading: false,
        error: null
    });

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const keyExtractorRef = useRef(keyExtractor);
    keyExtractorRef.current = keyExtractor;

    const hasFetchedRef = useRef(false);

    useEffect(() => {
        hasFetchedRef.current = false;
        setState({ extraItems: [], isLoading: false, error: null });
    }, [loader, seedCount]);

    const fetch = useCallback(async () => {
        if (!loader || hasFetchedRef.current) return;

        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        try {
            const extra = await loader({
                skip: seedCount,
                limit: 0,
                total: 0,
                loaded: seedCount
            });
            if (!mountedRef.current) return;
            hasFetchedRef.current = true;

            const seen = new Set<string>();
            const deduped = extra.filter((item) => {
                const key = keyExtractorRef.current(item);
                if (key === undefined) return true;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            setState({ extraItems: deduped, isLoading: false, error: null });
        } catch (err) {
            if (!mountedRef.current) return;
            setState({ extraItems: [], isLoading: false, error: err as Error });
        }
    }, [loader, seedCount]);

    useEffect(() => {
        if (!loader) return;

        if (typeof requestIdleCallback !== "undefined") {
            const id = requestIdleCallback(fetch);
            return () => cancelIdleCallback(id);
        }

        void fetch();
    }, [fetch, loader]);

    return state;
};

function defaultKeyExtractor(item: unknown): string | undefined {
    if (typeof item !== "object" || item === null) return undefined;
    const record = item as Record<string, unknown>;
    const sys = record["sys"];
    if (typeof sys === "object" && sys !== null) {
        const id = (sys as Record<string, unknown>)["id"];
        if (typeof id === "string") return id;
    }
    return undefined;
}
